import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/badges";
import { Copy, Loader2, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";

export function InvitesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [inviteForm, setInviteForm] = useState({ email: "", party_id: "" });
  const [inviteProjectFilter, setInviteProjectFilter] = useState("all");
  const [lastInviteLink, setLastInviteLink] = useState("");

  const { data: projects = [] } = useQuery({
    queryKey: ["admin-projects"],
    queryFn: async () => (await api.get("/admin/projects")).data,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["parties", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/parties`)).data,
    enabled: !!selectedId,
  });
  const { data: invites = [] } = useQuery({
    queryKey: ["admin-invites"],
    queryFn: async () => (await api.get("/admin/invites")).data,
  });

  const inviteMut = useMutation({
    mutationFn: async () => (await api.post("/admin/invites", inviteForm)).data,
    onSuccess: (d) => {
      toast.success(t("admin.toastInviteSent", { email: d.email }));
      setLastInviteLink(d.link);
      setInviteForm({ email: "", party_id: "" });
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const resendInviteMut = useMutation({
    mutationFn: async (id) => api.post(`/admin/invites/${id}/resend`),
    onSuccess: () => {
      toast.success(t("admin.toastInviteResent"));
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
      qc.invalidateQueries({ queryKey: ["admin-emp-invites"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success(t("admin.toastLinkCopied")));
  };

  const filteredInvites = inviteProjectFilter === "all" ? invites : invites.filter((i) => i.project_id === inviteProjectFilter);

  return (
    <>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t("admin.inviteCard")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("admin.partySelectedProject")}</Label>
              <Select value={inviteForm.party_id} onValueChange={(v) => setInviteForm({ ...inviteForm, party_id: v })}>
                <SelectTrigger data-testid="invite-party-select"><SelectValue placeholder={t("common.selectParty")} /></SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`invite-party-${p.id}`}>{p.company_name} ({p.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.email")}</Label>
              <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="person@company.com" data-testid="invite-email-input" />
            </div>
          </div>
          <Button onClick={() => inviteMut.mutate()} disabled={!inviteForm.party_id || !inviteForm.email || inviteMut.isPending} data-testid="invite-send-button">
            {inviteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("admin.sendInvite")}
          </Button>
          {lastInviteLink && (
            <div className="flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-2" data-testid="invite-link-box">
              <span className="flex-1 truncate font-mono text-xs text-slate-700">{lastInviteLink}</span>
              <Button variant="ghost" size="icon" onClick={() => copyLink(lastInviteLink)} data-testid="invite-copy-button"><Copy className="h-4 w-4" /></Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
          <CardTitle className="text-base">{t("admin.inviteHistory")}</CardTitle>
          <Select value={inviteProjectFilter} onValueChange={setInviteProjectFilter}>
            <SelectTrigger className="w-56" data-testid="invites-project-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="invites-filter-all">{t("admin.allProjects")}</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`invites-filter-${p.id}`}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t("common.email")}</TableHead><TableHead>{t("admin.thProject")}</TableHead><TableHead>{t("admin.thCompanyRole")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead>{t("admin.thLink")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {filteredInvites.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-500" data-testid="invites-empty">{t("admin.invitesEmpty")}</TableCell></TableRow>}
              {filteredInvites.map((i) => (
                <TableRow key={i.id} data-testid={`invite-row-${i.id}`}>
                  <TableCell className="font-medium">{i.email}</TableCell>
                  <TableCell className="text-sm">{i.project_name}</TableCell>
                  <TableCell className="text-sm">{i.company_name} · <span className="font-mono text-xs">{i.role}</span></TableCell>
                  <TableCell><StatusBadge status={i.status} data-testid={`invite-status-${i.id}`} /></TableCell>
                  <TableCell>
                    {i.status !== "accepted" && (
                      <div className="flex items-center gap-1">
                        {i.status === "pending" && (
                          <Button variant="ghost" size="icon" onClick={() => copyLink(i.link)} data-testid={`invite-copy-${i.id}`}><Copy className="h-4 w-4" /></Button>
                        )}
                        <Button variant="ghost" size="icon" title={t("admin.resendInviteT")} onClick={() => resendInviteMut.mutate(i.id)} disabled={resendInviteMut.isPending} data-testid={`invite-resend-${i.id}`}><RotateCw className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
